import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  IVA,
  IVAPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchIvas = (params?: GetIvasParams, enabled = true) => {
  return useQuery({
    queryKey: ['ivas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getIvas(params);
    },
    enabled,
  });
};

export const useGetIva = (id: string) => {
  return useQuery({
    queryKey: ['ivas', id],
    queryFn: async () => {
      return getIva(id);
    },
  });
};

export const useCreateIva = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createIva,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'IVA creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateIva = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: updateIva,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivas'] });
      navigate(returnUrl);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetIvasParams = Partial<IVA> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateIvaParams = Omit<IVA, 'id_iva'>;
export type UpdateIvaParams = {
  id: string;
  data: CreateIvaParams;
};

export const getIvas = async (params?: GetIvasParams) => {
  console.log(params)
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  
  const queryParams = getUrlParamsFetch(stateParams);
  return await get<IVAPagingRes>(
    `${urlBackend}api/v1/iva/?${queryParams}`,
    true
  );
};

export const getIva = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IVA>(`${urlBackend}api/v1/iva/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createIva = async (data: CreateIvaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<IVA>(`${urlBackend}api/v1/iva/`, data, true);
};

export const updateIva = async ({ id, data }: UpdateIvaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<IVA>(`${urlBackend}api/v1/iva/${id}/`, data, true);
};
